import React from 'react';

import { OpenInBrowser } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Collapse,
  Typography,
  darken,
  lighten,
  useTheme,
} from '@mui/material';
import InAppSpy from 'inapp-spy';
import { createScopedI18n } from '../i18n/i18n';
import OpenInBrowserDialog from './open_in_browser_dialog';

const inAppBrowserBannerI18n = createScopedI18n('components.in_app_browser_banner');

export type InAppBrowserBannerProps = {
  /**
   * If specified (`true` or `false`), controls the visibility of the banner.
   *
   * If not specified (or `undefined`), the banner will be shown when in-app browser is detected.
   */
  show?: boolean;
  /**
   * If `true`, the dialog to open in browser will be automatically shown when in-app browser is detected.
   */
  openDialogOnDetect?: boolean;
  /**
   * If `true`, the banner sticks to the top of the page while scrolling.
   */
  sticky?: boolean;
  /**
   * Top offset when `sticky` is enabled. Defaults to 0.
   */
  stickyTop?: number | string;
  inAppSpyOptions?: Parameters<typeof InAppSpy>[0];
  cardProps?: React.ComponentProps<typeof Card>;
};

const InAppBrowserBanner = ({
  show: showOverride,
  openDialogOnDetect,
  inAppSpyOptions,
  cardProps,
  sticky = false,
  stickyTop = 0,
}: InAppBrowserBannerProps) => {
  const theme = useTheme();

  const { isInApp } = InAppSpy(inAppSpyOptions);

  const show = showOverride ?? isInApp;

  const [showOpenBrowserDialog, setShowOpenBrowserDialog] = React.useState(false);

  React.useEffect(() => {
    if (openDialogOnDetect && isInApp) {
      setShowOpenBrowserDialog(true);
    }
  }, [openDialogOnDetect, isInApp]);

  // Adapt from @mui/material Alert.js
  const backgroundColor = React.useMemo(() => {
    const getBackgroundColor = theme.palette.mode === 'light' ? lighten : darken;
    return getBackgroundColor(theme.palette.warning.light, 0.75);
  }, [theme.palette.mode, theme.palette.warning.light]);

  return (
    <Box sx={[sticky && { position: 'sticky', top: stickyTop, zIndex: theme.zIndex.appBar }]}>
      <Collapse in={show} unmountOnExit>
        <Card
          square
          {...cardProps}
          sx={[{ backgroundColor }, ...(Array.isArray(cardProps?.sx) ? cardProps?.sx ?? [] : [cardProps?.sx])]}
        >
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              alignItems: 'center',
              paddingTop: 3,
              paddingBottom: 1,
            }}
          >
            <Avatar sx={{ backgroundColor: theme.palette.warning.main }}>
              <OpenInBrowser />
            </Avatar>

            <Typography variant="body2">{inAppBrowserBannerI18n('description')}</Typography>
          </CardContent>

          <CardActions sx={{ justifyContent: 'flex-end' }}>
            <Button variant="contained" disableElevation color="warning" onClick={() => setShowOpenBrowserDialog(true)}>
              {inAppBrowserBannerI18n('open_browser')}
            </Button>
          </CardActions>
        </Card>
      </Collapse>

      <OpenInBrowserDialog
        url={window.location.href}
        showInAppBrowserWarning
        open={showOpenBrowserDialog}
        onClose={() => setShowOpenBrowserDialog(false)}
      />
    </Box>
  );
};

export default InAppBrowserBanner;
